import React, { useContext, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { apiService } from "service/ApiService";
import { notificationService } from "service/NotificationService";
import StoreContext from "contexts/StoreContext";

import { useTranslation } from "react-i18next";

export default function ImportCsvModal({ type, title, show, handleClose, onImported }) {
  const {
    t,
  } = useTranslation();

  const { storeId } = useContext(StoreContext);
  const [file, setFile] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const resetFile = () => {
    setFile(null);
    setInputKey((prev) => prev + 1);
  };

  const handleExited = () => {
    resetFile();
    setErrorMessage("");
  };

  const handleFileChange = (event) => {
    const selected = event.target.files[0];
    setErrorMessage("");
    if (selected && !selected.name.toLowerCase().endsWith(".csv")) {
      notificationService.notifyError(t("messages.onlyCsvFilesAreSupported"));
      resetFile();
      return;
    }
    setFile(selected || null);
  };

  const downloadSample = async () => {
    setIsDownloading(true);
    try {
      const content = await apiService.get("api/v1/stores/import/sample", { type });
      // Excel needs a BOM to open UTF-8 (e.g. Greek) CSV files correctly.
      const csv = content.startsWith("﻿") ? content : "﻿" + content;
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${type}_sample.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      notificationService.notifyError(t("messages.unableToDownloadSample"));
    } finally {
      setIsDownloading(false);
    }
  };

  const upload = () => {
    const formData = new FormData();
    formData.append("file", file);

    setIsUploading(true);
    setErrorMessage("");
    apiService
      .post(`api/v1/stores/${storeId}/import/${type}`, formData)
      .then((response) => {
        notificationService.notifySuccess(t("messages.importCompletedSuccessfully", { created: response.created }));
        setIsUploading(false);
        if (onImported) onImported();
        handleClose();
      })
      .catch((error) => {
        setErrorMessage(error.message);
        notificationService.notifyError(t("messages.unableToImport"));
        setIsUploading(false);
      });
  };

  return (
    <Modal show={show} onHide={handleClose} onExited={handleExited} aria-labelledby="contained-modal-title-vcenter" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ margin: 0 }}>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">{t("messages.importCsvDescription")}</p>
        <Button variant="info" className="btn-outline mb-3" onClick={downloadSample} disabled={isDownloading}>
          <i className={isDownloading ? "fa fa-circle-notch fa-spin" : "fa fa-download"} />
          <span className="ml-1">{t("common.actions.downloadSample")}</span>
        </Button>
        {errorMessage ? <div style={{ color: "crimson" }}>{errorMessage}</div> : null}
        <Form onSubmit={(event) => event.preventDefault()}>
          <Form.Group>
            <label>{t("common.labels.csvFile")}</label>
            <Form.Control key={inputKey} type="file" accept=".csv,text/csv" onChange={handleFileChange} disabled={isUploading} />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="default" onClick={handleClose} disabled={isUploading}>
          {t("common.actions.cancel")}
        </Button>
        <Button className="btn-wd btn-outline" variant="success" onClick={upload} disabled={!file || !storeId || isUploading}>
          {isUploading ? (
            <div>
              <i className="fa fa-circle-notch fa-spin"></i>
              <span>{t("messages.pleaseWait")}</span>
            </div>
          ) : (
            <div>
              <i className="fa fa-upload" />
              <span>{t("common.actions.import")}</span>
            </div>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
